import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader } from "../../../components/ui/card_v2";
import { Input } from "../../../components/ui/input_v2";
import { Button } from "../../../components/ui/button";
import { Badge } from "../../../components/ui/badge_v3";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select_v3";
import { ArrowLeft, Coins, Plus, Search, Users } from "lucide-react";
import { ORGANIZATION_CREDITS, ROLE_CONFIG, SAMPLE_USERS } from "./credit-data";

import { CreditStats } from "./credit-stats";
import { UserCreditTable } from "./user-credit-table";
import { AllocateCreditsModal } from "./allocate-credits-modal";
import { EditAllocationModal } from "./edit-allocation-modal";
import { useThemeStore } from "../../../context/useThemeStore";
import { useLanguageStore } from "context/useLanguageStore";
import { useSchoolsStore } from "context/useSchoolsStore";
import { useUserStore } from "context/useUserStore";

import localization from "services/localization";
import SpecificAPI from "services/SpecificApi";
import { toast } from "react-toastify";

export default function CreditManagement() {
  const { currentLang } = useLanguageStore();
  const { schools, setSchools } = useSchoolsStore();
  const { userData, appUser } = useUserStore();

  const { theme } = useThemeStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRole, setSelectedRole] = useState("all");
  const [showAllocateModal, setShowAllocateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [orgCredits, setOrgCredits] = useState(ORGANIZATION_CREDITS);

  useEffect(() => {
    getUsersWalletData();
  }, []);

  const getUsersWalletData = () => {
    setLoading(true);
    var r0 = SpecificAPI.getAllUsers();
    var r1 = SpecificAPI.getAllWallets();

    var total = userData?.selected_school?.credit_amount ?? 0;
    var allocated = 0;
    var used = 0;

    Promise.all([r0, r1])
      .then((values) => {
        var userWallets = values[0]
          .filter((u) => u.role !== "student" && u.role !== "root")
          .map((user) => {
            var wallet = values[1].find((w) => w.user_id === user.id);
            var selectedSchoolWalletCredits = wallet?.credits.find((c) => c.school_id === userData?.selected_school_id);
            allocated +=
              (selectedSchoolWalletCredits?.credit_available ?? 0) + (selectedSchoolWalletCredits?.credit_spent ?? 0);
            used += selectedSchoolWalletCredits?.credit_spent ?? 0;
            return {
              ...user,
              allocated:
                (selectedSchoolWalletCredits?.credit_available ?? 0) + (selectedSchoolWalletCredits?.credit_spent ?? 0),
              used: selectedSchoolWalletCredits?.credit_spent ?? 0,
              remaining: selectedSchoolWalletCredits?.credit_available ?? 0,
              usageHistory: [],
              wallet: wallet,
            };
          });
        setUsers(userWallets);
        setOrgCredits({
          total: total,
          allocated: allocated,
          remaining: total - allocated,
          used: used,
          name: userData?.selected_school?.name,
        });
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const filteredUsers = useMemo(() => {
    let filtered = users;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((u) => u.name.toLowerCase().includes(term) || u.email.toLowerCase().includes(term));
    }
    if (selectedRole !== "all") {
      filtered = filtered.filter((u) => u.role === selectedRole);
    }
    return filtered;
  }, [users, searchTerm, selectedRole]);

  const handleAllocateToUser = (wallet = {}, userId, amount) => {
    setLoading(true);

    if (!wallet?.id) {
      wallet = {
        credits: [
          {
            school_id: userData?.selected_school_id,
            credit_available: amount,
            credit_frozen: 0,
            credit_spent: 0,
          },
        ],
        user_id: userId,
      };

      SpecificAPI.createWalletRecord(wallet)
        .then((res) => {
          setUsers((prev) =>
            prev.map((u) => (u.id === userId ? { ...u, allocated: u.spent + amount, remaining: amount } : u)),
          );
          setOrgCredits((prev) => ({
            ...prev,
            allocated: prev.allocated + amount,
            remaining: prev.remaining - amount,
          }));
          toast.success("Credits allocated successfully");
        })
        .catch((error) => {
          console.error(error);
          toast.error("Failed to allocate credits to user. Please try again.");
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      var selectedSchoolWallet = wallet.credits.find((c) => c.school_id === userData?.selected_school_id);
      if (selectedSchoolWallet) {
        selectedSchoolWallet.credit_available = amount;
        var newWallet = wallet.credits.filter((c) => c.id !== selectedSchoolWallet.id);
        newWallet.push(selectedSchoolWallet);
        wallet.credits = newWallet;
      } else {
        wallet.credits.push({
          school_id: userData?.selected_school_id,
          credit_available: amount,
          credit_frozen: 0,
          credit_spent: 0,
        });
      }

      SpecificAPI.updateWalletRecord(wallet)
        .then((res) => {
          setUsers((prev) =>
            prev.map((u) => {
              if (u.id !== userId) return u;
              const diff = amount - u.allocated;
              return { ...u, allocated: amount + u.used, remaining: amount };
            }),
          );
          const user = users.find((u) => u.id === userId);
          if (user) {
            const diff = amount - user.allocated;
            setOrgCredits((prev) => ({
              ...prev,
              allocated: prev.allocated + diff,
              remaining: prev.remaining - diff,
            }));
          }
          setEditingUser(null);
          toast.success("wallet updated successfully");
        })
        .catch((error) => {
          console.error(error);
          toast.error("Failed to update wallet. Please try again.");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleAllocateToRole = (wallet, role, amountPerUser) => {
    const roleUsers = users.filter((u) => u.role === role);
    const totalCost = roleUsers.length * amountPerUser;

    SpecificAPI.updateWalletRecordByRole(userData?.selected_school_id, role, amountPerUser)
      .then((res) => {
        setUsers((prev) =>
          prev.map((u) =>
            u.role === role ? { ...u, allocated: u.used + amountPerUser, remaining: amountPerUser } : u,
          ),
        );
        setOrgCredits((prev) => ({
          ...prev,
          allocated: prev.allocated + totalCost,
          remaining: prev.remaining - totalCost,
        }));
        toast.success("wallet updated successfully");
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to update wallet. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEditAllocation = (wallet, userId, newAllocation) => {
    if (!wallet?.id) {
      return handleAllocateToUser(wallet, userId, newAllocation);
    }

    var selectedSchoolWallet = wallet.credits.find((c) => c.school_id === userData?.selected_school_id);

    if (selectedSchoolWallet) {
      selectedSchoolWallet.credit_available = newAllocation;
      var newWallet = wallet.credits.filter((c) => c.id !== selectedSchoolWallet.id);
      newWallet.push(selectedSchoolWallet);
      wallet.credits = newWallet;
    } else {
      wallet.credits.push({
        school_id: userData?.selected_school_id,
        credit_available: newAllocation,
        credit_frozen: 0,
        credit_spent: 0,
      });
    }

    SpecificAPI.updateWalletRecord(wallet)
      .then((res) => {
        setUsers((prev) =>
          prev.map((u) => {
            if (u.id !== userId) return u;
            const diff = newAllocation - u.allocated;
            const data = { ...u, allocated: newAllocation + u.used, remaining: newAllocation };
            console.log("data", data);
            return data;
          }),
        );
        const user = users.find((u) => u.id === userId);
        if (user) {
          const diff = newAllocation - user.allocated;
          setOrgCredits((prev) => ({
            ...prev,
            allocated: prev.allocated + diff,
            remaining: prev.remaining - diff,
          }));
        }
        setEditingUser(null);
        toast.success("wallet updated successfully");
      })
      .catch((error) => {
        console.error(error);
        toast.error("Failed to update wallet. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const select_bg = theme === "dark" ? "bg-[var(--card-bg)] text-white" : "bg-white text-foreground";
  const select_bg2 = theme === "dark" ? "bg-[var(--card-bg)] text-white" : "text-foreground";

  return (
    <div className="mx-auto space-y-6 p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            {/* <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">{localization.back_to_organization || "Back to organization"}</span>
            </Button> */}
            <div>
              <h1 className={"text-2xl font-bold tracking-tight " + select_bg2}>
                {localization.credit_management || "Credit Management"}
              </h1>
              <p className={"text-sm " + select_bg2}>{localization.manage_your_credits || "Manage your credits"}</p>
            </div>
          </div>
        </div>
        <Button onClick={() => setShowAllocateModal(true)} className={"gap-2 self-start " + select_bg2}>
          <Plus className="h-4 w-4" />
          {localization.allocate_credits || "Allocate Credits"}
        </Button>
      </div>

      {/* Stats Cards */}
      {loading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="border shadow-sm">
              <CardContent className="p-5">
                <div className="h-20 animate-pulse rounded bg-muted" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <CreditStats
          total={orgCredits.total}
          allocated={orgCredits.allocated}
          used={orgCredits.used}
          remaining={orgCredits.remaining}
        />
      )}

      {/* Role Allocation Summary */}
      {!loading && (
        <div className={"grid grid-cols-2 gap-3 sm:grid-cols-4 "}>
          {Object.entries(ROLE_CONFIG).map(([role, config]) => {
            const roleUsers = users.filter((u) => u.role === role);
            const roleUsed = roleUsers.reduce((acc, u) => acc + u.used, 0);
            const roleAllocated = roleUsers.reduce((acc, u) => acc + u.allocated, 0);
            return (
              <Card key={role} className={"border shadow-sm transition-shadow hover:shadow-md pt-3 " + select_bg}>
                <CardContent className={"p-4 " + select_bg}>
                  <div className="flex items-center gap-2">
                    <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: config.color }} />
                    <span className="text-sm font-medium">{config.label}</span>
                    <Badge variant="secondary" className="ml-auto text-xs">
                      {roleUsers.length}
                    </Badge>
                  </div>
                  <div className="mt-2 flex items-baseline justify-between">
                    <span className="text-lg font-bold tabular-nums">{roleUsed.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground">/ {roleAllocated.toLocaleString()}</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* User Table Card */}
      <Card className={"border shadow-sm " + select_bg}>
        <CardHeader className={"border-b pb-4 " + select_bg}>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5 text-muted-foreground" />
                <h2 className="text-lg font-semibold">{localization.all_users || "All Users"}</h2>
              </div>
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <Input
                  placeholder={localization.search_users || "Search users..."}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={"h-9 pl-8 text-sm " + select_bg}
                />
              </div>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger className={"h-9 w-36 text-sm " + select_bg}>
                  <SelectValue placeholder={localization.filter_by_role || "Filter by role"} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{localization.all_roles || "All Roles"}</SelectItem>
                  {Object.entries(ROLE_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>
                      {config.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Badge variant="outline" className="self-start px-3 py-1 text-sm">
              {filteredUsers.length} {localization.user_count || "user"}
              {filteredUsers.length !== 1 ? "s" : ""}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className={"p-0 " + select_bg}>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="flex flex-col items-center gap-3">
                <Coins className="h-8 w-8 animate-pulse text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {localization.loading_credit_data || "Loading credit data..."}
                </p>
              </div>
            </div>
          ) : (
            <UserCreditTable users={filteredUsers} onEditUser={setEditingUser} />
          )}
        </CardContent>
      </Card>

      {/* Allocate Modal */}
      <AllocateCreditsModal
        open={showAllocateModal}
        onClose={() => setShowAllocateModal(false)}
        users={users}
        remainingOrgCredits={orgCredits.remaining}
        onAllocateToUser={handleAllocateToUser}
        onAllocateToRole={handleAllocateToRole}
      />

      {/* Edit Allocation Modal */}
      <EditAllocationModal
        open={!!editingUser}
        onClose={() => setEditingUser(null)}
        user={editingUser}
        remainingOrgCredits={orgCredits.remaining}
        onSave={handleEditAllocation}
      />
    </div>
  );
}
